import type { ComponentDeclaration } from "./profiles.ts";
/** Sessions as the sidebar lists them and the chat draws them. See CONTEXT.md "Agents". */

export const THINKING_LEVELS = ["off", "minimal", "low", "medium", "high", "xhigh"] as const;
export type ThinkingLevel = (typeof THINKING_LEVELS)[number];
/** The levels the judge routes a run between. */
export const ROUTED_THINKING_LEVELS = ["off", "low", "medium", "high"] as const;
export type RoutedThinking = (typeof ROUTED_THINKING_LEVELS)[number];

/** One provider/model/thinking triple: a profile default, or what a session was switched to. */
export interface ModelSpec {
	/** A pi-ai provider id, see `agent providers`. */
	provider: string;
	name: string;
	thinking: ThinkingLevel;
}

/** Who started a session. */
export type SessionOrigin = "user" | "schedule" | "loop" | "delegate";

export interface SessionSummary {
	id: string;
	profile: string;
	owner: string;
	title: string | null;
	createdBy: SessionOrigin;
	/** Who besides the owner may open and continue it. */
	sharedWith: string[];
	createdAt: number;
	updatedAt: number;
	archived: boolean;
	/** The project this session is filed under, fixed at creation. */
	projectId: string | null;
	/** Who sent the last message; where it changes a shared session gets a handover line. */
	lastSender: string | null;
	/** The model chosen in the chat UI; null follows the profile default. */
	model: ModelSpec | null;
	/** The session this one was branched from, and the last entry copied out of it. */
	parentId: string | null;
	forkEntryId: string | null;
	/** Anonymous: it reads no memory and is never scanned (ADR-0035). Fixed at creation. */
	anonymous: boolean;
	/** In the list only: its profile is not loaded right now. */
	profileMissing?: boolean;
}

/** What anyone looking at a session sees of its send lock. */
export interface LockView {
	holder: string;
	/** When the lock frees itself if nothing else happens. Never reached while a run is going. */
	idleExpiresAt: number;
}

/** Which roster model the judge put a memory scan on (ADR-0023). */
export interface Routed {
	model: string;
	/** Absent when the judge was not asked or did not answer. */
	thinking?: RoutedThinking;
	/** `unsure`: ruled under the threshold. `single`: one usable model, so only the thinking level was asked. */
	by: "judge" | "unsure" | "no-judge" | "failed" | "single";
	confidence?: number;
}

export interface TextContent { type: "text"; text: string }
export interface ImageContent { type: "image"; data: string; mimeType: string }
export interface ThinkingContent { type: "thinking"; thinking: string }
export interface ToolCallContent { type: "toolCall"; id: string; name: string; arguments: Record<string, unknown> }

export type AssistantBlock = TextContent | ThinkingContent | ToolCallContent;

/** `entryId` is the transcript entry a message came from, absent on one the transcript has not written yet. */
export interface UserMessage { role: "user"; content: string | (TextContent | ImageContent)[]; timestamp: number; entryId?: string }
export interface AssistantMessage {
	role: "assistant";
	entryId?: string;
	content: AssistantBlock[];
	model: string;
	provider: string;
	stopReason: string;
	/** A short line saying the run failed or was aborted; the provider's own text stays on the server. */
	errorMessage?: string;
	timestamp: number;
}
export interface ToolResultMessage {
	role: "toolResult";
	entryId?: string;
	toolCallId: string;
	toolName: string;
	content: (TextContent | ImageContent)[];
	details?: unknown;
	isError: boolean;
	timestamp: number;
}
/** A package's status line: a custom message it sent with `display: true`. */
export interface NoticeMessage { role: "custom"; content: string | (TextContent | ImageContent)[]; display: true; timestamp: number; entryId?: string }

/**
 * A transcript message as the chat receives it (src/server/chat-view.ts). Every other message --
 * a system prompt, a summary, a hidden custom message, a role added later -- stays on the server.
 */
export type ChatMessage = UserMessage | AssistantMessage | ToolResultMessage | NoticeMessage;

/**
 * A live session as the chat opens it. `M` is the message shape: pi's own in the runtime,
 * `ChatMessage` once the route has filtered it.
 */
/** The session's profile, with the components this session's tool calls are drawn with: exactly the ones it has. */
export interface SnapshotProfile {
	name: string;
	components: ComponentDeclaration[];
}

export interface Snapshot<M> {
	sessionId: string;
	profile: SnapshotProfile;
	messages: M[];
	isStreaming: boolean;
	activeTools: string[];
	model: { provider: string; id: string } | null;
	thinkingLevel: string;
	/** Who sent each message, by transcript entry id, in a shared session. */
	senders: Record<string, string>;
	/** A person chose this session's model in the picker, so it is not on its profile's. */
	pinned: boolean;
	/** Who may send right now; null while nobody holds the lock. Added by the route, beside the runtime. */
	lock?: LockView | null;
	/** The session each background job this session queued runs in, by job id. Added by the route. */
	jobSessions?: Record<string, string>;
}

/** What the platform adds to pi's own events on a session's `agent` stream. */
export type PlatformAgentEvent =
	| { type: "runtime_error"; message: string }
	/** A `delegate` call in this session has its child session; the card links it before the result is in. */
	| { type: "delegate_started"; callId: string; sessionId: string; profile: string }
	/** A background job this session queued has its session; the card of the call that queued it links it. */
	| { type: "delegate_job_started"; jobId: string; sessionId: string; profile: string }
	/** A `one_shot` call's run has started; the card shows that run's trace before the result is in. */
	| { type: "one_shot_started"; callId: string; loopId: string; iterationId: string }
	/** A `one_shot` call's run has ended and the platform starts its check `index` of `of`; the card names it. */
	| { type: "one_shot_checking"; callId: string; loopId: string; iterationId: string; index: number; of: number; run: string };

export interface User {
	id: string;
	name: string;
}
